import { z } from "zod";
import { Privacy } from "@/helpers/constants";

// Privacy Types
export type PrivacyLevel = typeof Privacy.PRIVATE | typeof Privacy.PUBLIC;

// Privacy-aware interface for items that have privacy settings
export interface PrivacyAware {
  privacy?: PrivacyLevel;
  user_id?: number;
}

export const BaseUserSchema = z.object({
  deleted_at: z.any().optional(),
  is_active: z.boolean().optional(),
  is_verified: z.boolean().optional(),
  is_invited: z.boolean().optional(),
  attributes: z
    .object({
      deactivated: z.boolean().optional(),
    })
    .nullable()
    .optional(),
  uuid: z.string(),
  id: z.number(),
  email: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  created_at: z.string().optional(),
  role_title: z.string().nullish(),
  role_source: z.string().nullish(),
  has_role_conflict: z.boolean().optional(),
  sso_resolved_role_id: z.number().nullish(),
  sso_resolved_role_title: z.string().nullish(),
  is_onboarded: z.boolean(),
  phone: z.string().nullable().optional(),
  display_name: z.string().nullable().optional(),
  last_login_at: z.string().nullable().optional(),
});

export type BaseUser = z.infer<typeof BaseUserSchema>;

// User List API Types
export const UserSchema = BaseUserSchema.extend({
  auth_token: z.string(),
  role_FK: z
    .object({
      permission: z.array(
        z.object({
          name: z.string(),
        })
      ),
      role_title: z.string(),
    })
    .optional(),
  permission: z.array(z.string()).nullable().optional(),
});

// get interface from UserSchema
export type User = z.infer<typeof UserSchema>;

// SuperTokens user info (for users not yet registered in DB)
export const SupertokensUserInfoSchema = z.object({
  email: z.string(),
});

export type SupertokensUserInfo = z.infer<typeof SupertokensUserInfoSchema>;

// SSO User Info response - either full user or just email for new users
export const SSOUserInfoResponseSchema = z.union([UserSchema, SupertokensUserInfoSchema]);

export type SSOUserInfoResponse = z.infer<typeof SSOUserInfoResponseSchema>;

export const UserListResponseSchema = z.object({
  users: z.array(BaseUserSchema),
  meta: z.object({
    count: z.number(),
    total_pages: z.number(),
  }),
});

export type UserListResponse = z.infer<typeof UserListResponseSchema>;

// User Management API Schemas

// Invite User
export const InviteUserResponseSchema = BaseUserSchema;

export type InviteUserResponse = z.infer<typeof InviteUserResponseSchema>;

// Update User
export const UpdateUserResponseSchema = z.object({
  ok: z.boolean(),
  user: BaseUserSchema.optional(),
});

export type UpdateUserResponse = z.infer<typeof UpdateUserResponseSchema>;

// Update User Role
export const UpdateUserRoleResponseSchema = z.object({
  ok: z.boolean(),
});

export type UpdateUserRoleResponse = z.infer<typeof UpdateUserRoleResponseSchema>;

// Delete User
export const DeleteUserResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().optional(),
});

export type DeleteUserResponse = z.infer<typeof DeleteUserResponseSchema>;

const TestMetadataSchema = z.object({
  name: z.string(),
});

const BaseAssetSchema = z.object({
  asset_rk: z.string(),
  asset_type: z.string(),
  upstream_count: z.number().default(0),
  downstream_count: z.number().default(0),
});

export const TableAssetSchema = BaseAssetSchema.extend({
  asset_type: z.literal("table"),
  table: z.string(),
  id: z.string().optional(),
  materialization: z.string().optional(),
  label: z.string(),
  schema_name: z.string().optional(),
  resource_type: z.string().optional(),
  tests: z.array(TestMetadataSchema).optional(),
});

export const DashboardAssetSchema = BaseAssetSchema.extend({
  asset_type: z.literal("dashboard"),
  asset_rk: z.string(),
  dashboard_id: z.string().nullish(),
  dashboard_name: z.string(),
  dashboard_url: z.string().optional(),
  workbook_name: z.string().optional(),
  description: z.string().optional(),
  created_by: z.string().optional(),
  last_modified: z.string().nullish(),
});

export const AssetSchema = z.discriminatedUnion("asset_type", [
  TableAssetSchema,
  DashboardAssetSchema,
]);

export type TableAsset = z.infer<typeof TableAssetSchema>;
export type DashboardAsset = z.infer<typeof DashboardAssetSchema>;
export type Asset = z.infer<typeof AssetSchema>;
